import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import CategoryCard from '../components/CategoryCard';
import ProductCard from '../components/ProductCard';
import Footer from '../components/Footer';
import { Tractor, Search, Sparkles } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const HERO_IMAGE_URL = 'https://static.prod-images.emergentagent.com/jobs/18087535-7274-4ce1-8fc3-44e53abf820b/images/57ecf6d541aef811e87bb9a1f74eff4c2c3ded94fe767e7f23535c73359d4a9d.png';

const HomePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [masterSearch, setMasterSearch] = useState('');
  const [masterResults, setMasterResults] = useState([]);
  const [masterLoading, setMasterLoading] = useState(false);
  const [blockedCategories, setBlockedCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [heroLoaded, setHeroLoaded] = useState(false);
  const debounceTimer = useRef(null);
  const masterTimer = useRef(null);

  // Preload hero image
  useEffect(() => {
    const img = new Image();
    img.onload = () => setHeroLoaded(true);
    img.src = HERO_IMAGE_URL;
  }, []);

  useEffect(() => { fetchCategories(); fetchBlockedCategories(); }, []);

  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      if (searchQuery.trim() === '') setFilteredCategories(categories);
      else {
        const q = searchQuery.toLowerCase();
        setFilteredCategories(categories.filter(cat => cat.name.toLowerCase().includes(q)));
      }
    }, 300);
    return () => { if (debounceTimer.current) clearTimeout(debounceTimer.current); };
  }, [searchQuery, categories]);

  useEffect(() => {
    if (masterTimer.current) clearTimeout(masterTimer.current);
    if (!masterSearch.trim()) { setMasterResults([]); return; }
    masterTimer.current = setTimeout(async () => {
      setMasterLoading(true);
      try {
        const res = await axios.get(`${BACKEND_URL}/api/products/search-all?q=${encodeURIComponent(masterSearch)}&limit=50`);
        setMasterResults(res.data);
      } catch { setMasterResults([]); }
      finally { setMasterLoading(false); }
    }, 400);
    return () => { if (masterTimer.current) clearTimeout(masterTimer.current); };
  }, [masterSearch]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/categories`);
      setCategories(response.data);
      setFilteredCategories(response.data);
    } catch (error) { console.error('Error fetching categories:', error); }
    finally { setLoading(false); }
  };

  const fetchBlockedCategories = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      if (token) {
        const res = await axios.get(`${BACKEND_URL}/api/category-access/me`, { headers: { Authorization: `Bearer ${token}` } });
        setBlockedCategories(res.data.blocked_categories || []);
      }
    } catch {}
  };

  const isBlocked = (catId) => blockedCategories.includes(catId);

  return (
    <div className="min-h-screen bg-[#F6F5F2] flex flex-col">
      {/* Hero Section */}
      <div
        className="relative h-[55vh] sm:h-[65vh] bg-cover bg-center"
        style={{
          backgroundImage: `url(${HERO_IMAGE_URL})`,
          backgroundColor: '#1A3626',
          opacity: heroLoaded ? 1 : 0.5,
          transition: 'opacity 0.3s ease-in',
        }}
        data-testid="hero-section"
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60"></div>
        <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-4">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white mb-3" style={{ fontFamily: 'Outfit, sans-serif', textShadow: '0 2px 8px rgba(0,0,0,0.4)' }} data-testid="hero-title">
            KRISHNA MACHINERY STORES
          </h1>
          <p className="text-sm sm:text-base text-white/90 mb-8 max-w-xl" style={{ fontFamily: 'Work Sans, sans-serif', textShadow: '0 1px 4px rgba(0,0,0,0.3)' }} data-testid="hero-subtitle">
            Your Trusted Partner for Agricultural & Industrial Machinery Solutions
          </p>
          <a
            href="#categories"
            className="bg-[#C05A3E] text-white hover:bg-[#A64B31] rounded-full px-8 py-3.5 font-semibold text-sm transition-all duration-200 inline-flex items-center gap-2 shadow-lg hover:shadow-xl hover:scale-105"
            data-testid="view-products-button"
          >
            <Tractor className="h-5 w-5" />
            View Products
          </a>
        </div>
      </div>

      {/* Master Search - Premium floating bar */}
      <div className="relative z-20 -mt-7 px-4 mb-4" data-testid="master-search-container">
        <div className="max-w-2xl mx-auto">
          <div className="relative group">
            <div className="absolute -inset-0.5 bg-gradient-to-r from-[#1A3626] via-[#C05A3E] to-[#1A3626] rounded-2xl opacity-60 blur-sm group-focus-within:opacity-100 transition-opacity duration-300"></div>
            <div className="relative flex items-center bg-white rounded-2xl shadow-xl border border-white/80">
              <Sparkles className="absolute left-4 h-5 w-5 text-[#C05A3E]" />
              <input
                type="text"
                placeholder="Master Search"
                value={masterSearch}
                onChange={(e) => setMasterSearch(e.target.value)}
                className="w-full pl-12 pr-16 py-4 bg-transparent rounded-2xl text-sm text-[#1A3626] placeholder-gray-400 focus:outline-none font-medium"
                data-testid="master-search-input"
              />
              {masterSearch && (
                <button onClick={() => setMasterSearch('')} className="absolute right-4 text-gray-400 hover:text-gray-600 text-sm font-medium">Clear</button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Master search results */}
      {masterSearch.trim() && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <h2 className="text-lg font-semibold text-[#1A3626] mb-4">
            {masterLoading ? 'Searching...' : `Results for "${masterSearch}" (${masterResults.length})`}
          </h2>
          {masterResults.length > 0 ? (
            <div className="flex flex-col gap-3 sm:grid sm:grid-cols-2 lg:grid-cols-3 sm:gap-4">
              {masterResults.map(p => <ProductCard key={p.id} product={p} />)}
            </div>
          ) : !masterLoading && (
            <p className="text-gray-500 text-center py-8">No products found matching "{masterSearch}"</p>
          )}
        </div>
      )}

      {/* Categories */}
      {!masterSearch.trim() && (
        <div id="categories" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 flex-1">
          <h2 className="text-xl sm:text-2xl lg:text-3xl font-semibold tracking-tight text-[#1A3626] mb-6 text-center" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="categories-heading">
            Product Categories
          </h2>
          <div className="max-w-xl mx-auto mb-6">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input type="text" placeholder="Search categories..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#1A3626] focus:ring-0 text-base" data-testid="category-search" />
            </div>
          </div>
          {loading ? (
            <div className="text-center py-12" data-testid="loading-categories"><p className="text-gray-500">Loading categories...</p></div>
          ) : filteredCategories.length === 0 ? (
            <div className="text-center py-12"><p className="text-gray-500 text-lg">No categories found matching "{searchQuery}"</p></div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4" data-testid="categories-grid">
              {filteredCategories.map(category => {
                const blocked = isBlocked(category.id);
                return blocked ? (
                  <div key={category.id} className="relative rounded-lg overflow-hidden">
                    <div className="opacity-30 grayscale">
                      <CategoryCard category={category} />
                    </div>
                    <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                      <span className="text-red-600 text-lg font-black tracking-widest" style={{ textShadow: '0 1px 3px rgba(255,255,255,0.8)' }}>BLOCKED</span>
                    </div>
                  </div>
                ) : (
                  <div key={category.id} className="cursor-pointer" onClick={() => navigate(`/category/${category.id}`)}>
                    <CategoryCard category={category} />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <Footer />
    </div>
  );
};

export default HomePage;
